import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus, InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationTemplate } from '@azure/msal-react';
import axios from 'axios';
import moment from 'moment-timezone';
import { Buffer } from 'buffer';
import { BiFileBlank, BiSearch, BiPencil, BiShow, BiHome, BiListUl, BiCalendarPlus, BiLogOut, BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import LoadingScreen from '../component/LoadingScreen';
import { acquireGetAccessToken } from '../utils/GetGrapApi';
import { checkRole } from '../callMsGroup';
import { MyFormItem } from '../types/template';
import Avatar from '../assets/images/users/avt1.png';

const PAGE_SIZE = 15;

function ResponseBadge({ f }: { f: MyFormItem }) {
    const now = new Date();
    const open = f.status === 'Published'
        && (!f.openDate  || now >= new Date(f.openDate))
        && (!f.closeDate || now <= new Date(f.closeDate));

    if (!f.hasSubmitted) {
        return (
            <span style={{ background: '#fff3e0', color: '#e65100', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                {open ? 'Pending' : 'Not submitted'}
            </span>
        );
    }
    if (f.canEdit && open) {
        return (
            <span style={{ background: '#e3f2fd', color: '#1565c0', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                Submitted · Editable
            </span>
        );
    }
    return (
        <span style={{ background: '#e8f5e9', color: '#2e7d32', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
            Submitted
        </span>
    );
}

export default function MyForms() {
    const { instance, accounts, inProgress } = useMsal();
    const navigate = useNavigate();

    const [loading, setLoading]     = useState(true);
    const [token, setToken]         = useState('');
    const [photoData, setPhotoData] = useState<string | null>(null);
    const [isAdmin, setIsAdmin]     = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [forms, setForms]         = useState<MyFormItem[]>([]);
    const [keyword, setKeyword]     = useState('');
    const [tab, setTab]             = useState<'pending' | 'submitted' | 'all'>('all');
    const [page, setPage]           = useState(0);

    const api = process.env.REACT_APP_API_END_POINT;

    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return;
        acquireGetAccessToken(instance, accounts).then(async (tokens: any) => {
            if (!tokens) return;
            try {
                const r = await axios('https://graph.microsoft.com/v1.0/me/photo/$value', {
                    headers: { Authorization: `Bearer ${tokens.accessTokenProfile}` },
                    responseType: 'arraybuffer',
                });
                setPhotoData('data:image/jpeg;base64, ' + Buffer.from(r.data, 'binary').toString('base64'));
            } catch { /* no photo */ }
            const role = await checkRole(tokens.accessTokenProfile);
            setIsAdmin(role === 'admin');
            setIsSuperAdmin(role === 'superadmin');
            setToken(tokens.accessToken);
        });
    }, [inProgress, instance, accounts]); // eslint-disable-line

    useEffect(() => {
        if (!token) return;
        axios.get<MyFormItem[]>(`${api}/v1/Form/myForms`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => setForms(res.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [token]); // eslint-disable-line

    // Reset page when filter/search changes
    useEffect(() => { setPage(0); }, [keyword, tab]);

    if (loading) return <LoadingScreen />;

    const isOpen = (f: MyFormItem) => {
        const now = new Date();
        return f.status === 'Published'
            && (!f.openDate  || now >= new Date(f.openDate))
            && (!f.closeDate || now <= new Date(f.closeDate));
    };

    const kw = keyword.trim().toLowerCase();
    const filtered = forms.filter(f => {
        if (kw && !f.name.toLowerCase().includes(kw) && !f.templateCode.toLowerCase().includes(kw)) return false;
        if (tab === 'pending'   && f.hasSubmitted) return false;
        if (tab === 'submitted' && !f.hasSubmitted) return false;
        return true;
    });

    const totalPages   = Math.ceil(filtered.length / PAGE_SIZE);
    const paged        = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    const pendingCount = forms.filter(f => !f.hasSubmitted && f.status === 'Published').length;
    const submittedCount = forms.filter(f => f.hasSubmitted).length;

    // Build visible page numbers (max 5 slots)
    const pageNumbers: (number | '…')[] = [];
    if (totalPages <= 7) {
        for (let i = 0; i < totalPages; i++) pageNumbers.push(i);
    } else {
        pageNumbers.push(0);
        if (page > 2) pageNumbers.push('…');
        for (let i = Math.max(1, page - 1); i <= Math.min(totalPages - 2, page + 1); i++) pageNumbers.push(i);
        if (page < totalPages - 3) pageNumbers.push('…');
        pageNumbers.push(totalPages - 1);
    }

    return (
        <div className="mf-page">
            <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
                <header className="he-topbar">
                    <div className="he-topbar-brand" onClick={() => navigate(isSuperAdmin ? '/forms' : '/')}>
                        <img src="/logo-pandora.png" alt="Pandora" className="he-topbar-brand-logo" />
                    </div>
                    <nav className="he-topbar-nav">
                        {!isSuperAdmin && (
                            <>
                                <button className="he-topbar-nav-item" onClick={() => navigate('/')}>
                                    <BiHome size={16} /> <span>Home</span>
                                </button>
                                {!isAdmin && (
                                    <button className="he-topbar-nav-item active" onClick={() => navigate('/myforms')}>
                                        <BiFileBlank size={16} /> <span>My Forms</span>
                                    </button>
                                )}
                            </>
                        )}
                        {isSuperAdmin && (
                            <button className="he-topbar-nav-item" onClick={() => navigate('/forms')}>
                                <BiCalendarPlus size={16} /> <span>Forms</span>
                            </button>
                        )}
                        {isAdmin && (
                            <>
                                <button className="he-topbar-nav-item" onClick={() => navigate('/myevents')}>
                                    <BiListUl size={16} /> <span>My Events</span>
                                </button>
                                <button className="he-topbar-nav-item" onClick={() => navigate('/admin')}>
                                    <BiCalendarPlus size={16} /> <span>Admin</span>
                                </button>
                            </>
                        )}
                    </nav>
                    <div className="he-topbar-right">
                        <img src={photoData || Avatar} className="he-topbar-avatar" alt="avatar" />
                        <span className="he-topbar-username">{accounts[0]?.name}</span>
                        <button
                            className="he-topbar-logout"
                            title="Logout"
                            onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: '/' })}
                        >
                            <BiLogOut size={18} />
                        </button>
                    </div>
                </header>
            </MsalAuthenticationTemplate>

            <main className="mf-main">
                <div className="mf-container">
                    <div className="mf-heading">
                        <h1 className="mf-title">My Forms</h1>
                        <p className="mf-subtitle">
                            {pendingCount > 0
                                ? `${pendingCount} form${pendingCount !== 1 ? 's' : ''} waiting for your response`
                                : 'All forms assigned to you'}
                        </p>
                    </div>

                    {/* Toolbar */}
                    <div className="mf-toolbar">
                        <div className="mf-search">
                            <BiSearch className="mf-search__icon" />
                            <input
                                type="text"
                                className="mf-search__input"
                                placeholder="Search forms…"
                                value={keyword}
                                onChange={e => setKeyword(e.target.value)}
                            />
                            {keyword && (
                                <button className="mf-search__clear" onClick={() => setKeyword('')}>×</button>
                            )}
                        </div>
                        <div className="mf-tabs">
                            <button className={`mf-tab${tab === 'all' ? ' mf-tab--active' : ''}`} onClick={() => setTab('all')}>
                                All ({forms.length})
                            </button>
                            <button className={`mf-tab${tab === 'pending' ? ' mf-tab--active' : ''}`} onClick={() => setTab('pending')}>
                                Pending ({pendingCount})
                            </button>
                            <button className={`mf-tab${tab === 'submitted' ? ' mf-tab--active' : ''}`} onClick={() => setTab('submitted')}>
                                Submitted ({submittedCount})
                            </button>
                        </div>
                    </div>

                    {/* List */}
                    {filtered.length === 0 ? (
                        <div className="mf-empty">
                            <BiFileBlank size={48} color="#ccc" />
                            <div>{tab === 'pending' ? "You're all caught up!" : 'No forms found'}</div>
                        </div>
                    ) : (
                        <div className="mf-list">
                            {paged.map(f => (
                                <div
                                    key={f.templateCode}
                                    className="mf-card"
                                    onClick={() => navigate(`/form/${f.templateCode}`)}
                                >
                                    <div className="mf-card__icon">
                                        <BiFileBlank size={24} color="#c5281f" />
                                    </div>
                                    <div className="mf-card__body">
                                        <div className="mf-card__name">{f.name}</div>
                                        <div className="mf-card__meta">
                                            {f.templateCode}
                                            {f.description ? ` — ${f.description}` : ''}
                                        </div>
                                    </div>
                                    <div className="mf-card__badge">
                                        <ResponseBadge f={f} />
                                    </div>
                                    <div className="mf-card__dates">
                                        {f.hasSubmitted && (
                                            <div className="mf-card__date-row">
                                                <span className="mf-card__date-label">Submitted</span>
                                                <span>{moment.utc(f.updatedAt ?? f.submittedAt).tz('Asia/Bangkok').format('DD MMM YYYY HH:mm')}</span>
                                            </div>
                                        )}
                                        {f.closeDate && (
                                            <div className="mf-card__date-row">
                                                <span className="mf-card__date-label">Closes</span>
                                                <span>{moment.utc(f.closeDate).tz('Asia/Bangkok').format('DD MMM YYYY')}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="mf-card__action">
                                        {!f.hasSubmitted && isOpen(f) ? <BiPencil size={18} /> : <BiShow size={18} />}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="mf-pagination">
                            <button
                                className="mf-page-btn mf-page-btn--nav"
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={page === 0}
                            >
                                <BiChevronLeft size={18} />
                            </button>

                            {pageNumbers.map((n, i) =>
                                n === '…' ? (
                                    <span key={`ellipsis-${i}`} className="mf-page-ellipsis">…</span>
                                ) : (
                                    <button
                                        key={n}
                                        className={`mf-page-btn${page === n ? ' mf-page-btn--active' : ''}`}
                                        onClick={() => setPage(n as number)}
                                    >
                                        {(n as number) + 1}
                                    </button>
                                )
                            )}

                            <button
                                className="mf-page-btn mf-page-btn--nav"
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={page === totalPages - 1}
                            >
                                <BiChevronRight size={18} />
                            </button>
                        </div>
                    )}

                    <div className="mf-footer">
                        {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length} form{filtered.length !== 1 ? 's' : ''}
                    </div>
                </div>
            </main>

            <footer className="he-footer">
                <div className="he-footer-inner">
                    <div className="he-footer-brand">
                        <img src="/logo-pandora.png" alt="Pandora" className="he-footer-logo-img" />
                        <span className="he-footer-tagline">Internal event management platform</span>
                    </div>
                    <nav className="he-footer-links">
                        <button onClick={() => navigate('/')}>Home</button>
                        {isAdmin && (
                            <button onClick={() => navigate('/myevents')}>My Events</button>
                        )}
                    </nav>
                    <div className="he-footer-copy">
                        © {new Date().getFullYear()} PPV &mdash; All rights reserved
                    </div>
                </div>
            </footer>
        </div>
    );
}
